/*
 * The boot: from a session to a live, persisted model. It decides the boot path — cold when there
 * is no stored model for this account and this MODEL_VERSION, warm when there is — opens the
 * socket, and runs the catch-up the socket asks for after `rt.hello`: `rt.bootstrap` then
 * `rt.sync{since: payload.eventId}` on a cold boot, `rt.sync{since: meta.lastEventId, convSeq}`
 * on a warm one (protocol.md §2.8, the client half is normative). A `resync{all}` at any point is
 * a cold boot over the same connection.
 *
 * The bootstrap reducer reads core's fields into the model and hands `modules[<id>]` to each
 * module's `onBootstrap` untouched — core does not know a module's shape (S0-PLAN R-O).
 *
 * Revocation (`session.revoked`, close 4401) wipes the model on disk and keeps drafts and the
 * outbox; the shell routes to the login page. A stale synchroniser token (close 4403) is fetched
 * again once, because a login in another tab rotates it; a second refusal stops the socket.
 */

import type { ModuleRegistry } from "../modules/registry.ts"
import type { Db } from "../persist/idb.ts"
import { clearModel, loadModel, startPersistence } from "../persist/persist.ts"
import { emptyModel, type ConnectionState, type Model } from "../store/model.ts"
import type { ClientStore } from "../store/store.ts"
import {
	CloseOriginOrCSRF,
	eventFromEnvelope,
	type BootstrapPayload,
	type SyncResult,
} from "../wire/index.ts"
import { api, setCsrfToken } from "./api.ts"
import { Socket, type SocketConstructor, type SocketStatus } from "./socket.ts"

/** `convSeq` is omitted above this many conversations; the server answers with a resync instead. */
export const CONV_SEQ_LIMIT = 200

/** What `GET /api/auth/session` answers (protocol.md §3.2). */
export interface SessionAnswer {
	user: { id: number; locale: string } | null
	csrfToken: string
}

export interface BootDeps {
	client: ClientStore
	registry: ModuleRegistry
	/** The signed-in account; the boot never runs without one. */
	session: { userId: number; csrf: string; locale?: string }
	/** The open database, or `undefined` for a browser that refuses site data. */
	db: Db | undefined
	/** `web/<sha>`; the build this client names in its hello, kept by the server for the record. */
	build: string
	/** Called after a revocation, once the model is wiped: the shell routes to the login page. */
	onRevoked?: () => void
	WebSocket?: SocketConstructor
	url?: string
	random?: () => number
	now?: () => number
}

export interface Runtime {
	socket: Socket
	/** True when this boot started from an empty model. */
	cold: boolean
	stop(): void
}

export async function boot(deps: BootDeps): Promise<Runtime> {
	const { client, registry, db } = deps
	let csrf = deps.session.csrf
	let csrfRetried = false

	const stored = db ? await loadModel(db, deps.session.userId, registry.all()) : null
	const cold = stored === null
	client.replaceModel({
		...(stored ?? emptyModel()),
		persistence: db ? "idb" : "memory",
		connection: "connecting",
	})

	// True whenever the next catch-up must be a bootstrap: at a cold start, after a whole-scope
	// resync, and until a cold boot has run to completion.
	let needsColdBoot = cold
	let coldBootInFlight: Promise<void> | undefined
	const coldBoot = (socket: Socket): Promise<void> => {
		if (coldBootInFlight) return coldBootInFlight
		coldBootInFlight = runColdBoot(socket).finally(() => {
			coldBootInFlight = undefined
		})
		return coldBootInFlight
	}
	const runColdBoot = async (socket: Socket): Promise<void> => {
		needsColdBoot = true
		const payload = await socket.send("rt.bootstrap", {})
		client.replaceModel(applyBootstrap(client.model, payload))
		for (const mod of registry.all()) {
			mod.onBootstrap?.(payload.modules[mod.id], client.api)
		}
		const res = await socket.send("rt.sync", { sinceEventId: payload.eventId })
		needsColdBoot = false
		applySync(res, socket)
	}
	/** A cold boot nobody awaits: if the connection goes meanwhile, the next handshake runs it. */
	const restart = (socket: Socket): void => {
		coldBoot(socket).catch(() => {})
	}
	const warmSync = async (socket: Socket): Promise<void> => {
		applySync(await socket.send("rt.sync", syncRequest(client.model)), socket)
	}
	/** protocol.md §2.8 `applySync`; a whole-scope resync restarts the cold path. */
	const applySync = (res: SyncResult, socket: Socket): void => {
		if (res.resync?.scope === "all") {
			restart(socket)
			return
		}
		for (const env of res.events) client.dispatch(eventFromEnvelope(env))
		for (const conv of res.resync?.convs ?? []) client.requestResync(conv)
		client.update((model) =>
			res.eventId > model.meta.lastEventId
				? { ...model, meta: { ...model.meta, lastEventId: res.eventId } }
				: model,
		)
	}

	const socket = new Socket({
		csrf: () => csrf,
		client: deps.build,
		...(deps.session.locale === undefined ? {} : { locale: deps.session.locale }),
		catchUp: (s) => (needsColdBoot ? coldBoot(s) : warmSync(s)),
		onHello: (hello) => {
			client.update((model) => ({
				...model,
				limits: hello.limits,
				meta: { ...model.meta, serverBootId: hello.serverBootId },
			}))
		},
		onEvent: (event) => {
			client.dispatch(event)
		},
		onStatus: (status) => {
			client.update((model) => {
				const connection = connectionOf(status)
				return model.connection === connection ? model : { ...model, connection }
			})
			if (status.kind === "revoked") void revoked()
			if (status.kind === "stopped" && status.code === CloseOriginOrCSRF) void refreshCsrf()
		},
		...(deps.WebSocket === undefined ? {} : { WebSocket: deps.WebSocket }),
		...(deps.url === undefined ? {} : { url: deps.url }),
		...(deps.random === undefined ? {} : { random: deps.random }),
		...(deps.now === undefined ? {} : { now: deps.now }),
	})

	/**
	 * The model goes, typed text stays (protocol.md §2.11). Persistence stops first and its last
	 * write lands before the wipe: a write after it would leave a cursor with no model behind it,
	 * and the next sign-in in this tab would catch up over nothing.
	 */
	const revoked = async (): Promise<void> => {
		await stopPersistence()
		if (db) await clearModel(db)
		deps.onRevoked?.()
	}
	const refreshCsrf = async (): Promise<void> => {
		if (csrfRetried) return
		csrfRetried = true
		try {
			const answer = await api.get<SessionAnswer>("/api/auth/session")
			if (!answer.user) return
			csrf = answer.csrfToken
			setCsrfToken(csrf)
			socket.connect()
		} catch {
			// The session is gone or the server unreachable; the shell's next request finds out.
		}
	}

	client.setResyncHandlers({
		all: () => {
			needsColdBoot = true
			if (socket.currentStatus().kind === "open") restart(socket)
		},
	})

	// The model that came off the disk is what the first write is compared against: everything it
	// holds is on disk already, so a warm boot's first flush writes what the boot changed instead of
	// rewriting every row it has just read (a second after the boot, while the socket catches up).
	// The modules are the same list the boot read the slices back with: each says what of its own
	// slice is worth a disk, and the layer writes that and compares against it.
	const stopPersistence = db
		? startPersistence(db, client.api, { saved: stored, owners: registry.all() })
		: () => Promise.resolve()
	socket.connect()
	return {
		socket,
		cold,
		stop() {
			socket.dispose()
			void stopPersistence()
		},
	}
}

// ---- the reducers of the two catch-up replies ----

/**
 * Which modules the instance runs, as the payload names them, or null when it names none.
 *
 * The field is read off the payload rather than through the generated type because it is the one
 * thing here that a *server* may be too old to send: an instance running a build from before it
 * existed says nothing, and saying nothing means "draw everything", which is what the client did
 * before this answer was asked for at all (`Model.enabledModules`). An entry that is not a string
 * is dropped rather than trusted — this list decides what the rail offers.
 */
export function enabledModulesOf(payload: BootstrapPayload): readonly string[] | null {
	const named = (payload as { enabledModules?: unknown }).enabledModules
	if (!Array.isArray(named)) return null
	return named.filter((id): id is string => typeof id === "string")
}

/**
 * The bootstrap reducer: core's fields, whole; groups minus tombstones; the cursor at the
 * payload's `eventId`. Module slices and conversation state are the modules' to rebuild through
 * `onBootstrap`; drafts and the outbox are the local layer's and untouched.
 */
export function applyBootstrap(model: Model, payload: BootstrapPayload): Model {
	const users: Model["users"] = {}
	for (const user of payload.users) users[user.id] = user
	const groups: Model["groups"] = {}
	for (const group of payload.groups) if (group.deletedAt === undefined) groups[group.id] = group
	return {
		...model,
		me: payload.me,
		users,
		groups,
		conversations: {},
		conversationOrder: [],
		messages: {},
		modules: {},
		enabledModules: enabledModulesOf(payload),
		settings: payload.settings,
		notificationPrefs: payload.notificationPrefs,
		limits: payload.limits,
		activityUnread: payload.activityUnread,
		meta: {
			...model.meta,
			lastEventId: payload.eventId,
			serverBootId: payload.serverBootId,
		},
	}
}

/** The warm boot's request: the cursor, and per-conversation seqs while there are few enough. */
export function syncRequest(model: Model): {
	sinceEventId: number
	convSeq?: Record<string, number>
} {
	const entries = Object.entries(model.conversations)
	if (entries.length > CONV_SEQ_LIMIT) return { sinceEventId: model.meta.lastEventId }
	const convSeq: Record<string, number> = {}
	for (const [id, slice] of entries) convSeq[id] = slice.lastSeq
	return { sinceEventId: model.meta.lastEventId, convSeq }
}

/** The model's one word for where a socket stands; a visitor's page follows its socket by it too. */
export function connectionOf(status: SocketStatus): ConnectionState {
	switch (status.kind) {
		case "idle":
		case "connecting":
			return "connecting"
		case "catchup":
			return "catchup"
		case "open":
			return "open"
		case "offline":
		case "stopped":
			return "offline"
		case "draining":
			return "draining"
		case "revoked":
			return "revoked"
	}
}
